import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { RequireAuth } from '@/auth/RequireAuth';
import { AppShell } from '@/components/AppShell';
import { ErrorState } from '@/components/ErrorState';
import { FullPageMessage } from '@/components/FullPageMessage';
import { ChartSkeleton } from '@/components/skeletons';
import { Card } from '@/components/ui/card';
import { useMe } from '@/api/queries';
import { defaultProject, findWorkspace, WorkspaceProvider } from '@/lib/workspace';
import { AuthCallback } from '@/pages/AuthCallback';
import { NotFound } from '@/pages/NotFound';
import { Onboarding } from '@/pages/Onboarding';

/**
 * A pure SPA with client side routing, per plan D27.
 *
 * Next.js `output: 'export'` cannot build `/p/:projectId/events/:name` without
 * `generateStaticParams`, and every interesting route in this product is
 * dynamic, so a static export would have failed at build time on the first
 * useful screen. The hosting island serves `index.html` for every path.
 *
 * Onboarding is imported eagerly and every analytics screen is lazy. That
 * split is deliberate: a brand new account is redirected straight to
 * `/onboarding`, and making the product's first moment of proof wait on a
 * charting library it does not use would be the wrong trade. Recharts loads
 * when the first chart is actually asked for.
 */

const Overview = lazy(async () => ({ default: (await import('@/pages/Overview')).Overview }));
const Live = lazy(async () => ({ default: (await import('@/pages/Live')).Live }));
const EventDetail = lazy(async () => ({
  default: (await import('@/pages/EventDetail')).EventDetail,
}));
const People = lazy(async () => ({ default: (await import('@/pages/People')).People }));
const PersonDetail = lazy(async () => ({
  default: (await import('@/pages/PersonDetail')).PersonDetail,
}));
const Sessions = lazy(async () => ({ default: (await import('@/pages/Sessions')).Sessions }));
const Heatmaps = lazy(async () => ({
  default: (await import('@/pages/Heatmaps')).Heatmaps,
}));
const Attribution = lazy(async () => ({
  default: (await import('@/pages/Attribution')).Attribution,
}));
const Audience = lazy(async () => ({
  default: (await import('@/pages/Audience')).Audience,
}));
const Funnels = lazy(async () => ({ default: (await import('@/pages/Funnels')).Funnels }));
const Keys = lazy(async () => ({ default: (await import('@/pages/settings/Keys')).Keys }));
const ProjectSettingsPage = lazy(async () => ({
  default: (await import('@/pages/settings/Project')).ProjectSettingsPage,
}));

/**
 * The fallback while a route chunk downloads. It is the same skeleton shape the
 * screen itself uses while its data loads, so a slow connection sees one
 * continuous loading state rather than two different ones in sequence.
 */
function RouteFallback(): ReactNode {
  return (
    <Card>
      <ChartSkeleton />
    </Card>
  );
}

function Lazy({ children }: { children: ReactNode }): ReactNode {
  return <Suspense fallback={<RouteFallback />}>{children}</Suspense>;
}

/** `/` resolves the caller's default project and gets out of the way. */
function LandingRedirect() {
  const me = useMe();

  if (me.isPending) {
    return <FullPageMessage title="Loading your workspace" busy />;
  }
  if (me.isError) {
    return (
      <main className="flex min-h-full items-center justify-center px-6">
        <ErrorState error={me.error} onRetry={() => void me.refetch()} />
      </main>
    );
  }

  const target = defaultProject(me.data);
  if (target === null) {
    // The lazy bootstrap in GET /v1/me creates an org, a Default project and
    // three write keys on first authenticated request (FR-AUTH-4), so an empty
    // response means that bootstrap did not run, not that the user has nothing.
    return (
      <FullPageMessage
        tone="destructive"
        title="Your workspace is not ready"
        detail="Your account exists but no organisation was created for it. Reloading runs the setup again."
      />
    );
  }

  const path = `/p/${encodeURIComponent(target.project.project_id)}${
    target.project.first_event_at === null ? '/onboarding' : ''
  }`;
  return <Navigate to={path} replace />;
}

/**
 * Resolves `:projectId` against the caller's memberships before any child
 * renders. A project id that is not in `/v1/me` renders "not found", which is
 * the same answer as another tenant's project by design (plan 4.4).
 */
function ProjectLayout() {
  const params = useParams<{ projectId: string }>();
  const projectId = params.projectId ?? '';
  const me = useMe();

  if (me.isPending) {
    return <FullPageMessage title="Loading your workspace" busy />;
  }
  if (me.isError) {
    return (
      <main className="flex min-h-full items-center justify-center px-6">
        <ErrorState error={me.error} onRetry={() => void me.refetch()} />
      </main>
    );
  }

  const workspace = findWorkspace(me.data, projectId);
  if (workspace === null) return <NotFound />;

  return (
    <WorkspaceProvider workspace={workspace}>
      <AppShell me={me.data} workspace={workspace} />
    </WorkspaceProvider>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Outside the auth gate: the callback IS the authentication. */}
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route
        path="/"
        element={
          <RequireAuth>
            <LandingRedirect />
          </RequireAuth>
        }
      />

      <Route
        path="/p/:projectId"
        element={
          <RequireAuth>
            <ProjectLayout />
          </RequireAuth>
        }
      >
        <Route
          index
          element={
            <Lazy>
              <Overview />
            </Lazy>
          }
        />
        <Route path="onboarding" element={<Onboarding />} />
        <Route
          path="live"
          element={
            <Lazy>
              <Live />
            </Lazy>
          }
        />
        {/* Event names contain spaces and colons in the estate already, and a
            `#` would otherwise open a fragment. The link builders encode, and
            React Router decodes, so the handler receives the real name. */}
        <Route
          path="events/:eventName"
          element={
            <Lazy>
              <EventDetail />
            </Lazy>
          }
        />
        <Route
          path="people"
          element={
            <Lazy>
              <People />
            </Lazy>
          }
        />
        <Route
          path="people/:distinctId"
          element={
            <Lazy>
              <PersonDetail />
            </Lazy>
          }
        />
        <Route
          path="funnels"
          element={
            <Lazy>
              <Funnels />
            </Lazy>
          }
        />
        <Route
          path="sessions"
          element={
            <Lazy>
              <Sessions />
            </Lazy>
          }
        />
        <Route
          path="acquisition"
          element={
            <Lazy>
              <Attribution />
            </Lazy>
          }
        />
        <Route
          path="audience"
          element={
            <Lazy>
              <Audience />
            </Lazy>
          }
        />
        <Route
          path="heatmaps"
          element={
            <Lazy>
              <Heatmaps />
            </Lazy>
          }
        />
        <Route
          path="settings/keys"
          element={
            <Lazy>
              <Keys />
            </Lazy>
          }
        />
        <Route
          path="settings/project"
          element={
            <Lazy>
              <ProjectSettingsPage />
            </Lazy>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
